import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export type PickerPanelCoords = {
  top: number;
  left: number;
  width: number;
};

type UsePickerPopoverOptions = {
  estimatedHeight?: number;
  minWidth?: number;
  maxWidth?: number;
};

export function usePickerPopover(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  {
    estimatedHeight = 380,
    minWidth = 280,
    maxWidth = 360,
  }: UsePickerPopoverOptions = {},
) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelCoords, setPanelCoords] = useState<PickerPanelCoords | null>(null);

  const updatePanelPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const width = Math.min(maxWidth, Math.max(minWidth, rect.width, window.innerWidth - 16));
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < estimatedHeight && rect.top > estimatedHeight;
    const top = openUp ? Math.max(8, rect.top - estimatedHeight - 4) : rect.bottom + 4;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);

    setPanelCoords({ top, left, width });
  }, [estimatedHeight, maxWidth, minWidth, triggerRef]);

  useEffect(() => {
    if (!open) {
      setPanelCoords(null);
      return;
    }
    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, updatePanelPosition]);

  return { panelRef, panelCoords, updatePanelPosition };
}
